import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Undo2, Loader2, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { PhotoUploader } from "../components/PhotoUploader";

export default function ReactivationWorkflow() {
  const { frontId } = useParams();
  const navigate = useNavigate();
  const [front, setFront] = useState(null);
  const [etat, setEtat] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [anomalieId, setAnomalieId] = useState("");
  const [actionCorrective, setActionCorrective] = useState("");
  const [photosAvant, setPhotosAvant] = useState([]);
  const [photosApres, setPhotosApres] = useState([]);
  const [purge, setPurge] = useState(false);
  const [inspectionId, setInspectionId] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [nouveauStatut, setNouveauStatut] = useState("AUTORISEE");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/fronts/${frontId}`).then(({ data }) => {
      setFront(data.front);
      setEtat(data.etat);
      setAnomalies(data.anomalies.filter((a) => a.Statut === "OUVERTE"));
      setInspections(data.inspections);
    });
  }, [frontId]);

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/fronts/${frontId}/request-reactivation`, {
        anomalie_id: anomalieId, action_corrective: actionCorrective, photos_avant: photosAvant,
        photos_apres: photosApres, purge_effectuee: purge, nouvelle_inspection_id: inspectionId,
        commentaire, nouveau_statut: nouveauStatut,
      });
      setDone(data);
    } catch (err) {
      setError(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!front || !etat) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement...</div>;
  }

  if (etat.Statut_Global !== "SUSPENDUE") {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center text-slate-400">
        Ce front n'est pas suspendu, la remise en exploitation n'est pas nécessaire.
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
        <h1 className="text-2xl font-black uppercase text-slate-100">Front remis en exploitation</h1>
        <p className="text-slate-400">Nouveau statut : <b className="text-emerald-400">{done.nouveau_statut}</b></p>
        <Button data-testid="reactivation-done-back" onClick={() => navigate(`/front/${frontId}`)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase">
          Retour à la fiche du front
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-red-400 mb-1">Remise en exploitation</div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-50">{front.Front_Libelle}</h1>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Anomalie ayant provoqué la suspension</label>
          <select
            data-testid="reactivation-anomalie-select"
            value={anomalieId}
            onChange={(e) => setAnomalieId(e.target.value)}
            className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100"
          >
            <option value="">— Sélectionner —</option>
            {anomalies.map((a) => <option key={a.Anomalie_ID} value={a.Anomalie_ID}>{a.Description} ({a.Gravite})</option>)}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Action corrective réalisée</label>
          <Textarea data-testid="reactivation-action-corrective" value={actionCorrective} onChange={(e) => setActionCorrective(e.target.value)} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Photos avant</label>
            <PhotoUploader photos={photosAvant} onChange={setPhotosAvant} testId="reactivation-photos-avant" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Photos après</label>
            <PhotoUploader photos={photosApres} onChange={setPhotosApres} testId="reactivation-photos-apres" />
          </div>
        </div>

        <label className="flex items-center gap-2.5 text-sm text-slate-200 cursor-pointer">
          <Checkbox data-testid="reactivation-purge-checkbox" checked={purge} onCheckedChange={setPurge} />
          Purge réalisée si nécessaire
        </label>

        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Nouvelle inspection (optionnel)</label>
          <select
            data-testid="reactivation-inspection-select"
            value={inspectionId}
            onChange={(e) => setInspectionId(e.target.value)}
            className="w-full bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100"
          >
            <option value="">— Aucune —</option>
            {inspections.map((i) => <option key={i.Inspection_ID} value={i.Inspection_ID}>{i.Date} {i.Heure} — {i.Inspecteur_Nom} ({i.Score}%)</option>)}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Commentaire</label>
          <Textarea data-testid="reactivation-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-mono uppercase tracking-widest text-slate-400">Nouveau statut</label>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="reactivation-statut-autorisee"
              onClick={() => setNouveauStatut("AUTORISEE")}
              className={`flex-1 rounded-lg border-2 py-3 text-sm font-bold uppercase ${nouveauStatut === "AUTORISEE" ? "bg-emerald-500 text-black border-emerald-500" : "border-slate-700 text-slate-400"}`}
            >
              🟢 Autorisée
            </button>
            <button
              type="button"
              data-testid="reactivation-statut-reserves"
              onClick={() => setNouveauStatut("RESERVES")}
              className={`flex-1 rounded-lg border-2 py-3 text-sm font-bold uppercase ${nouveauStatut === "RESERVES" ? "bg-amber-500 text-black border-amber-500" : "border-slate-700 text-slate-400"}`}
            >
              🟠 Avec réserves
            </button>
          </div>
        </div>
      </div>

      {error && <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">{error}</div>}

      <Button
        data-testid="reactivation-submit-button"
        disabled={submitting}
        onClick={handleSubmit}
        className="w-full h-12 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wide"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-4 h-4 mr-2" /> Valider la remise en exploitation</>}
      </Button>
    </div>
  );
}
